import React from 'react';
import { isPast, setHours } from 'date-fns';

import { EstimatedDeliveryDateRange, FlightStatus, TimeWindow } from 'models/shipments';
import { formatTimeWindow, getEddRangeText, getEddText, isToday } from 'utils/date.utils';

import {
  expectedTodayTooltipContent,
  lateTooltipContent,
  onTimeTooltipContent,
  updatedTooltipContent,
} from './edd.constants';

export interface EddTagInfo {
  estimatedDeliveryPrefix?: string;
  estimatedDeliveryText: string;
  flightStatus: FlightStatus | null;
  cssClassName: string;
  tooltipContent: React.ReactNode | string;
}

interface ArticleEddDeps {
  dateRevised: boolean;
  deliveredByDateISO: string | null;
  estimatedDeliveryDateRange: EstimatedDeliveryDateRange | null;
  timeWindow: TimeWindow | null;
}
export const createEddTagInfo = (article: Partial<ArticleEddDeps>): EddTagInfo | null => {
  const { dateRevised = false, deliveredByDateISO, estimatedDeliveryDateRange: eddRange, timeWindow } = article;

  const { passedWindow, deliveryDate, start, end } = timeWindow || {};

  if (passedWindow && deliveryDate && !isToday(deliveryDate)) return generateDeliveryDateTag(deliveryDate, dateRevised);
  if (start && end) return generateTimeWindowTag(start, end);
  if (eddRange?.toDate && eddRange?.fromDate)
    return generateDeliveryDateTag(eddRange.toDate, dateRevised, eddRange.fromDate);
  if (deliveredByDateISO) return generateDeliveryDateTag(deliveredByDateISO, dateRevised);
  return null;
};

const generateDeliveryDateTag = (date: string, dateRevised: boolean, fromDate?: string): EddTagInfo | null => {
  const deliveryDate = setHours(new Date(date), 12);
  if (isToday(date)) return null;
  const eddTagInfoMapping: EddTagInfo = {
    estimatedDeliveryPrefix: dateRevised ? 'Now expected' : 'Expected',
    estimatedDeliveryText: fromDate ? getEddRangeText(fromDate, date) : getEddText(date),

    flightStatus: dateRevised ? FlightStatus.UPDATED : FlightStatus.ON_TIME,
    cssClassName: 'eddTagGreen',
    tooltipContent: dateRevised ? updatedTooltipContent : onTimeTooltipContent,
  };
  if (isPast(deliveryDate)) {
    eddTagInfoMapping.estimatedDeliveryPrefix = 'Was expected';
    eddTagInfoMapping.flightStatus = FlightStatus.LATE;
    eddTagInfoMapping.cssClassName = 'eddTagOrange';
    eddTagInfoMapping.tooltipContent = lateTooltipContent;
  }
  return eddTagInfoMapping;
};

const generateTimeWindowTag = (start: string, end: string): EddTagInfo | null => {
  const windowStart = formatTimeWindow(start);
  const windowEnd = formatTimeWindow(end);

  const timeWindowInfoMapping: EddTagInfo = {
    estimatedDeliveryText: `${windowStart}–${windowEnd}`,
    flightStatus: FlightStatus.EXPECTED_TODAY,
    cssClassName: 'eddTagGreen',
    tooltipContent: expectedTodayTooltipContent,
  };
  return timeWindowInfoMapping;
};
